import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import {
  type FieldErrors,
  type FieldValues,
  type UseFormReturn,
  FormProvider,
  useFormState,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { X, Plus } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { focusFirstErrorField } from "@/common/utils/form";
import { useConfirmDialogs } from "@/hooks/use-confirm-dialog";
import { ScrollFadeArea } from "./ScrollFadeArea";

export interface FormModalProps<TFormValues extends FieldValues> {
  /** 모달 제목 */
  title: string;
  /** 트리거 버튼 라벨 */
  triggerLabel: string;
  /** 등록 확인 다이얼로그 제목 */
  confirmTitle: string;
  /** 등록 확인 다이얼로그 설명 (선택) — 강조 색상 등이 필요하면 ReactNode 로 전달 */
  confirmDescription?: React.ReactNode;
  /** react-hook-form 인스턴스 */
  form: UseFormReturn<TFormValues>;
  /** 폼 초기값 (닫기 시 리셋용) */
  defaultValues: TFormValues;
  /** create mutation의 mutate 함수 */
  mutate: (
    data: TFormValues,
    options: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess: (res: any) => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => void;
    },
  ) => void;
  /** 등록 성공 시 콜백 (toast 표시 등) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccessCallback: (res: any) => void;
  /** 등록 실패 시 콜백 (toast 표시 등) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onErrorCallback: (error: any) => void;
  /** 커스텀 트리거 버튼 (생략 시 기본 gradient 버튼) */
  trigger?: React.ReactNode;
  /** 쓰기 권한. false 면 트리거를 아예 그리지 않는다 */
  canWrite?: boolean;
  /**
   * 권한은 있으나 상황상 등록할 수 없는 사유 (로딩 중, 소속이 소유자가 아님 등).
   * 주면 트리거가 사유 툴팁과 함께 비활성으로 남는다 — 권한 부족과 구분한다
   */
  writeDisabledReason?: string;
  /** Dialog Content 너비 등 오버라이드 */
  containerClassName?: string;
  /** 스크롤 래퍼 className 오버라이드 (기본: overflow-y-auto) */
  scrollAreaClassName?: string;
  /** 내부 콘텐츠 레이아웃 오버라이드 */
  contentClassName?: string;
  /** 폼 검증 실패 시 커스텀 에러 핸들러 (제공 시 기본 토스트 대신 호출) */
  onFormError?: (
    errors: FieldErrors<TFormValues>,
    values: TFormValues,
  ) => void;
  /** 하단 제출 버튼 라벨 */
  submitLabel?: string;
  /** 제목 오른쪽에 붙는 요소 (예: 안내 툴팁 뱃지) */
  titleExtra?: React.ReactNode;
  /** 폼 상태와 무관하게 제출을 막아야 할 때 (예: 변경 대상이 0건) */
  submitDisabled?: boolean;
  /** 제출 버튼 아래 한 줄 안내 */
  submitHint?: React.ReactNode;
  /** 제출 확인 다이얼로그의 확인 버튼 문구 (기본: "등록하기") */
  confirmButtonText?: string;
  /**
   * 열림 상태를 외부에서 제어할 때 사용 (드롭다운 메뉴 등에서 여는 경우).
   * onOpenChange 와 함께 주면 트리거 버튼을 그리지 않는다 — 여닫는 책임이 호출자에게 있다
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 폼 컨텐츠 */
  children: React.ReactNode;
}

/**
 * 등록 모달 래퍼 컴포넌트
 * Dialog 셸, 확인 다이얼로그, 폼 제출 플로우 등 공통 보일러플레이트를 처리
 *
 * @example
 * ```tsx
 * <FormModal
 *   title="충전소 등록"
 *   triggerLabel="충전소 등록"
 *   confirmTitle="충전소를 등록할까요?"
 *   form={form}
 *   defaultValues={DEFAULT}
 *   mutate={mutate}
 *   onSuccess={(res) => toast.success(`‘${res.name}’ 충전소를 등록했어요.`)}
 *   onError={() => toast.error("충전소 등록에 실패했습니다.")}
 * >
 *   <Section title="기본 정보">
 *     <FormTextInput name="name" ... />
 *   </Section>
 * </FormModal>
 * ```
 */
export function FormModal<TFormValues extends FieldValues>({
  title,
  triggerLabel,
  confirmTitle,
  confirmDescription,
  form,
  defaultValues,
  mutate,
  onSuccessCallback,
  onErrorCallback,
  trigger,
  canWrite = true,
  writeDisabledReason,
  containerClassName,
  scrollAreaClassName,
  contentClassName,
  onFormError: onFormErrorProp,
  submitLabel = "등록",
  titleExtra,
  submitDisabled = false,
  submitHint,
  confirmButtonText = "등록하기",
  open: controlledOpen,
  onOpenChange: onOpenChangeProp,
  children,
}: FormModalProps<TFormValues>) {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (isControlled) onOpenChangeProp?.(value);
    else setInternalOpen(value);
  };
  // 확인 다이얼로그가 닫힌 뒤에도 등록 버튼은 응답이 올 때까지 살아 있다 —
  // 이 플래그가 없으면 빠르게 다시 눌러 레코드가 중복 생성된다
  const [isMutating, setIsMutating] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const { handleSubmit, reset, control, setFocus } = form;
  const { isDirty, isSubmitting } = useFormState({ control });

  const [formErrors, setFormErrors] =
    React.useState<FieldErrors<TFormValues> | null>(null);

  const onFormError: Parameters<typeof handleSubmit>[1] = (errors) => {
    if (onFormErrorProp) {
      onFormErrorProp(errors, form.getValues());
    } else {
      toast.error("필수 항목을 모두 입력해 주세요.");
    }
    setFormErrors(errors);
  };

  React.useEffect(() => {
    if (formErrors) {
      focusFirstErrorField(formErrors, setFocus, {
        scrollContainer: scrollRef.current,
      });
    }
  }, [formErrors, setFocus]);

  const dialogs = useConfirmDialogs({
    exit: {
      title: `편집 중인 내용이 있어요.\n저장하지 않고 나갈까요?`,
      description: "저장하지 않은 내용은 삭제돼요.",
      confirmText: "나가기",
      cancelText: "취소",
      variant: "danger",
    },
    confirm: {
      title: confirmTitle,
      description: confirmDescription,
      confirmText: confirmButtonText,
      cancelText: "취소",
      variant: "primary",
    },
  });

  const handleCloseAttempt = () => {
    if (isDirty) {
      dialogs.open("exit", handleClose);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    setIsMutating(false);
    setOpen(false);
  };

  const onFormSubmit = (data: TFormValues) => {
    dialogs.open("confirm", () => handleConfirmSubmit(data));
  };

  const handleConfirmSubmit = async (data: TFormValues) => {
    // 중복 제출을 실제로 막는 지점 — 아래 버튼 disabled 는 눈에 보이는 피드백일 뿐이다
    if (isMutating) return;
    setIsMutating(true);
    mutate(data, {
      onSuccess: (res) => {
        setIsMutating(false);
        onSuccessCallback(res);
        handleClose();
      },
      onError: (error) => {
        setIsMutating(false);
        onErrorCallback(error);
      },
    });
  };

  // 권한이 없으면 트리거조차 그리지 않는다 — 쓸 수 없는 기능은 존재를 알리지 않는다
  if (!canWrite) return null;

  // 권한은 있으나 상황상 못 쓰는 경우만 사유를 남긴 비활성 트리거로 그린다
  if (writeDisabledReason) {
    return (
      <Button
        theme="gradient"
        variant="default"
        size="md"
        disabled
        title={writeDisabledReason}
      >
        <Plus />
        {triggerLabel}
      </Button>
    );
  }

  return (
    <>
      <DialogPrimitive.Root
        open={open}
        onOpenChange={(val) => {
          if (val) {
            setOpen(true);
            // 요청 도중 닫았다 다시 열어도 등록 버튼이 비활성으로 남지 않게 한다
            setIsMutating(false);
          } else handleCloseAttempt();
        }}
      >
        {!isControlled && (
          <DialogPrimitive.Trigger asChild>
            {trigger ?? (
              <Button theme="gradient" variant="default" size="md">
                <Plus />
                {triggerLabel}
              </Button>
            )}
          </DialogPrimitive.Trigger>
        )}
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-50 bg-bg-dim",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            )}
          />
          <DialogPrimitive.Content
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              handleCloseAttempt();
            }}
            onPointerDownOutside={(e) => {
              e.preventDefault();
              handleCloseAttempt();
            }}
            onFocusOutside={(e) => {
              e.preventDefault();
            }}
            className={cn(
              "fixed left-1/2 top-1/2 z-50",
              "-translate-x-1/2 -translate-y-1/2",
              "w-200 max-h-[85vh]",
              "bg-bg-primary rounded-2xl overflow-hidden",
              "inset-border-solid-normal",
              "flex flex-col",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "outline-none",
              containerClassName,
            )}
          >
            <DialogPrimitive.Description className="sr-only">
              {title} 폼
            </DialogPrimitive.Description>

            {/* Header */}
            <div className="flex items-center gap-2 px-10 pt-10 pb-5 shrink-0 inset-border-solid-normal-b">
              <div className="flex flex-1 items-center gap-2 min-w-0">
                <DialogPrimitive.Title className="typo-heading-1-bold text-labels-primary">
                  {title}
                </DialogPrimitive.Title>
                {titleExtra}
              </div>
              <button
                type="button"
                onClick={handleCloseAttempt}
                className="shrink-0 size-6 flex items-center justify-center"
              >
                <X size={24} color="labels-assistive" />
              </button>
            </div>

            <FormProvider {...form}>
              <form
                onSubmit={handleSubmit(onFormSubmit, onFormError)}
                className="flex flex-col flex-1 min-h-0"
              >
                {/* Contents - Scrollable */}
                <ScrollFadeArea
                  wrapperClassName="flex-1 min-h-0 flex flex-col"
                  scrollRef={scrollRef}
                  className={cn(
                    "flex-1 min-h-0 px-10 pt-6 pb-9",
                    scrollAreaClassName,
                  )}
                >
                  <div className={cn("flex flex-col gap-6", contentClassName)}>
                    {children}
                  </div>
                </ScrollFadeArea>

                {/* Footer - Submit Button */}
                <div className="flex flex-col items-center gap-2.5 px-10 pt-6 pb-10 shrink-0">
                  <Button
                    type="submit"
                    theme="gradient"
                    variant="default"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting || isMutating || submitDisabled}
                  >
                    {isSubmitting || isMutating ? "등록 중..." : submitLabel}
                  </Button>
                  {submitHint && (
                    <p className="typo-label-1-medium text-labels-alternative text-center">
                      {submitHint}
                    </p>
                  )}
                </div>
              </form>
            </FormProvider>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <dialogs.ConfirmDialogs />
    </>
  );
}

FormModal.displayName = "FormModal";
